import React from 'react';
import moment from 'jalali-moment';
import Dialog from '../../toolkit/dialog';
import Content from '../dashboard/Content';
import MonitorSummary from './MonitorSummary';
import DataDialog from '../common/data-dialog/DataDialog';
import monitorService from '../../services/monitor-service';
import { CommonKeys } from '../../constants';
import { DatabaseSchemaStat } from '../interfaces';
import { DEFAULT_BACKGROUND_COLORS, DEFAULT_BORDER_COLORS } from '../../utils/simple-chart';
import { humanReadableSize, numberWithCommas, reOrderArray } from '../../utils/easy';

import './style';

export interface MonitorState {
  storage: {
    diskaPath: string;
    size: number;
    free: number;
  };
  memory: {
    free: number;
    total: number;
    usage: number;
  };
  cpu: {
    count: number;
    usage: number;
    model: string;
  };
  logs: DatabaseSchemaStat;
  users: DatabaseSchemaStat;
  groups: DatabaseSchemaStat;
  alarms: DatabaseSchemaStat;
  sensors: DatabaseSchemaStat;
  templates: DatabaseSchemaStat;
  extra: any;
}

export default class Monitor extends React.Component<any, MonitorState> {

  state = {
    storage: {
      diskaPath: '/',
      size: 100,
      free: 50
    },
    memory: {
      free: 0,
      total: 1,
      usage: 50
    },
    cpu: {
      count: 1,
      usage: 50,
      model: 'Unknown CPU'
    },
    logs: { count: 0, size: 0 },
    users: { count: 0, size: 0 },
    groups: { count: 0, size: 0 },
    sensors: { count: 0, size: 0 },
    alarms: { count: 0, size: 0 },
    templates: { count: 0, size: 0 },
    extra: {
      sensor: {
        total: 'Messages.FetchMessage',
        unknown: 'Messages.FetchMessage'
      }
    }
  }

  render() {
    const state = this.state;
    return (
      <Content title="مانیتور سیستم">
        <div className="cmp-monitor">
          <div className="row">
            {this.renderStorageSummery()}
            {this.renderCpuSummery()}
            {this.renderMemorySummery()}
          </div>
          <hr style={{ marginBottom: '2rem' }} />
          <div className="row">
            <MonitorSummary
              icon="fal fa-users"
              text={numberWithCommas(state.users.count)}
              desc="تعداد کاربران"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.users.size)}
              onClick={this.handleDatabaseCardClicked.bind(this, CommonKeys.User)}
            />
            <MonitorSummary
              icon="fal fa-database"
              text={numberWithCommas(state.logs.count)}
              desc="تعداد لاگ‌ها"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.logs.size)}
              onClick={this.handleDatabaseCardClicked.bind(this, CommonKeys.Log)}
            />
            <MonitorSummary
              icon="fal fa-temperature-frigid"
              text={numberWithCommas(state.sensors.count)}
              desc="تعداد سنسورها"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.sensors.size)}
              onClick={this.handleDatabaseCardClicked.bind(this, CommonKeys.Sensor)}
            />
            <MonitorSummary
              icon="fal fa-archive"
              text={numberWithCommas(state.groups.count)}
              desc="تعداد گروه‌ها"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.groups.size)}
              onClick={this.handleDatabaseCardClicked.bind(this, CommonKeys.Group)}
            />
            <MonitorSummary
              icon="fal fa-cog"
              text={numberWithCommas(state.templates.count)}
              desc="تعداد الگوها"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.templates.size)}
            />
            <MonitorSummary
              text={numberWithCommas(state.alarms.count)}
              desc="تعداد هشدارها"
              subDesc={'فضای اشغال شده: ' + humanReadableSize(state.alarms.size)}
              icon="fal fa-exclamation-triangle"
            />
          </div>
        </div>
      </Content>
    );
  }

  componentDidMount = async () => {
    const { extra } = this.state;
    monitorService.systemInfo().then(data => this.setState(data));
    monitorService.database().then(data => this.setState(data));
    monitorService.databaseSensor().then(data => this.setState({ extra: { ...extra, ...{ sensor: data } } }));
  }

  private renderStorageSummery = () => {
    const { size, free } = this.state.storage;
    const freePercent = free * 100 / size;
    return (
      <MonitorSummary
        text={`${humanReadableSize(size - free)}/${humanReadableSize(size)}`}
        desc="Storage - فضای ذخیره سازی"
        subDesc=""
        icon="fal fa-disc-drive"
        progress
        progressValue={100 - freePercent} />
    );
  }

  private renderCpuSummery = () => {
    const { model, usage } = this.state.cpu;
    return (
      <MonitorSummary
        text={`${usage}% در حال استفاده`}
        desc={`پردازنده - ${model}`}
        subDesc=""
        icon="fal fa-memory"
        progress
        progressValue={usage} />
    );
  }

  private renderMemorySummery = () => {
    const { total, free, usage } = this.state.memory;
    return (
      <MonitorSummary
        text={`${humanReadableSize((total - free) * 1024 * 1024)}/${humanReadableSize(total * 1024 * 1024)}`}
        desc="Memory - حافظه سیستم"
        subDesc=""
        icon="fal fa-memory"
        progress
        progressValue={usage} />
    );
  }

  private handleDatabaseCardClicked = async (key: string) => {
    let data: any, table: any[], graphs: any[];
    switch (key) {
      case CommonKeys.Group:
        data = await monitorService.databaseGroup();
        table = [
          { key: 'تعداد کل گروه‌ها', value: data.total },
          { key: 'تعداد زیرگروه‌ها', value: data.hasParentCount },
          { key: 'گروهای دارای تصویر', value: data.hasMediaCount }
        ];
        break;
      case CommonKeys.Sensor:
        data = await monitorService.databaseSensor();
        table = [
          { key: 'تعداد کل سنسورها', value: data.total },
          { key: 'تعداد سنسورهای ناشناخته', value: data.unknown },
          { key: 'تعداد سنسورهای در حال آلارم', value: data.alarmActiveCount }
        ];
        break;
      case CommonKeys.User:
        data = await monitorService.databaseUser();
        const hourlyGraph = [];
        for (const key in data.hourlyGraph) {
          let final = key.replace('range-', '').split('-').reverse().map(x => x + ':00').join(' - ');
          hourlyGraph.push({ key: final, value: data.hourlyGraph[key] });
        }
        const colors = [...DEFAULT_BACKGROUND_COLORS];
        const borderColors = [...DEFAULT_BORDER_COLORS];
        const usersGraph = data.usersGraph;
        table = [
          { key: 'تعداد مشتریان', value: usersGraph.customersCount },
          { key: 'تعداد سوپروایزرها', value: usersGraph.subCustomersCount },
          { key: 'تعداد کاربران مدیر', value: usersGraph.adminsCount },
          { key: 'تعداد کل کاربران', value: usersGraph.count },
        ];
        graphs = [
          {
            color: colors[5],
            borderColor: borderColors[5],
            title: 'گراف روزانه زمان ورود کاربران به سیستم در یک ماه گذشته',
            datasetLabel: 'تعداد لاگین',
            data: data.dailyGraph.map(x => ({
              key: moment.from(x.dateTime, 'en').format('jYYYY-jMM-jDD'),
              value: x.count
            }))
          },
          {
            data: hourlyGraph,
            datasetLabel: 'تعداد لاگین',
            title: 'گراف زمان لاگین در بازه زمانی ۲۴ ساعته در یک ماه گذشته',
            color: colors[11],
            borderColor: borderColors[11],
          },
          {
            type: 'pie',
            data: data.browsers.map(x => ({ key: x.name, value: x.count })),
            title: 'گراف میزان استفاده از مرورگرهای مختلف در ۳ ماه گذشته',
            color: reOrderArray(colors, 2),
            borderColor: reOrderArray(borderColors, 2),
            step: 3
          },
          {
            type: 'pie',
            data: data.platforms.map(x => ({ key: x.name, value: x.count })),
            title: 'گراف میزان استفاده از پلتفرم‌های مختلف در ۳ ماه گذشته',
            step: 3
          }
        ];
        break;
      case CommonKeys.Log:
        data = await monitorService.databaseLog();
        table = [
          { key: 'تعداد کل لاگ های ثبت شده', value: data['total'] },
          { key: 'تعداد لاگ های ثبت شده در ۱ ماه گذشته', value: data['lastMonth'] },
          { key: 'تعداد لاگ های ثبت شده در ۳ ماه گذشته', value: data['lastSeason'] },
          { key: 'تعداد لاگ های ثبت شده در ۶ ماه گذشته', value: data['lastHalfYear'] },
        ];
        break;
    }

    const dialog = new Dialog({
      rtl: true,
      size: 'large',
      title: this.getDialogTitle(key),
      message: <DataDialog table={table} graphs={graphs} />,
    });
    dialog.show()
  }

  private getDialogTitle = (key: string) => {
    switch (key) {
      case CommonKeys.Log:
        return 'اطلاعات لاگ‌های ثبت شده';
      case CommonKeys.Sensor:
        return 'اطلاعات سنسورها';
      case CommonKeys.User:
        return 'اطلاعات کاربران';
      case CommonKeys.Alarm:
        return 'اطلاعات هشدارها';
      case CommonKeys.Group:
        return 'اطلاعات گروه‌ها';
      case CommonKeys.Firmware:
        return 'اطلاعات فرم‌ورها';
      case CommonKeys.Preference:
        return 'اطلاعات تظیمات';
      case CommonKeys.Template:
        return 'اطلاعات الگوها';
      default:
        return 'Unknown';
    }
  }

}
